/**
 * Schema-driven control panel — view layer only.
 *
 * ExhibitApp owns sim/render/preset/dirty. This panel:
 *   1. Builds widgets from shared/ui/controls-schema.json
 *   2. Emits intent via onControlPatch(path, value, opts)
 *   3. Pulls display state via syncFromApp(...) — never while a control is
 *      mid-gesture (pointer/keyboard), so sliders are never clobbered mid-drag
 *
 * Single entry for param edits: handlers.onControlPatch → app.patchControl.
 */

import {
  allControlAvailability,
  controlAvailability,
  type ControlAvailability,
} from "../help/controlAvailability";
import {
  allControlDefs,
  CONTROL_SECTIONS,
  type ControlDef,
  type OptionsFrom,
} from "../help/controlsSchema";
import { getParamHelp } from "../help/glossary";
import {
  applyGuideChrome,
  getGuideMode,
  listGuideModes,
  type GuideMode,
} from "../help/guideModes";
import { getPath } from "../help/pathAccess";
import {
  initRegistry,
  modelRegistry,
  REQUIRES_TYPES,
  topologyRegistry,
} from "../plugins";
import { getPreset, PRESETS, type Preset } from "../presets";
import {
  cloneAudioParams,
  defaultAudioParams,
  type AudioParams,
} from "../audio/params";
import {
  cloneRenderParams,
  cloneSimParams,
  type RenderParams,
  type SimParams,
} from "../sim/params";
import { EquationPanel } from "./equationPanel";

/** FOV preset sugar → degrees (Track Y2). Kept here as display mapping only. */
const FOV_PRESET: Record<string, number> = {
  ortho: 0,
  mild: 35,
  cinematic: 55,
};

export type ControlPatchOpts = { reset?: boolean };

export type ControlHandlers = {
  /** Single pluggable entry for all schema control edits. */
  onControlPatch: (
    path: string,
    value: unknown,
    opts?: ControlPatchOpts,
  ) => void;
  onPreset: (preset: Preset) => void;
  onPlayToggle: () => void;
  onStep: () => void;
  onReset: () => void;
  onReseed: () => void;
  onStepsPerFrame: (n: number) => void;
  onShare: () => void;
  onGuideMode?: (modeId: string) => void;
};

function $(id: string): HTMLElement {
  const el = document.getElementById(id);
  if (!el) throw new Error(`#${id} missing`);
  return el;
}

function fmt(n: number, digits = 3): string {
  if (Number.isInteger(n)) return String(n);
  return n
    .toFixed(digits)
    .replace(/\.?0+$/, (m) => (m.startsWith(".") ? "" : m));
}

function registryOptions(
  from: OptionsFrom,
  guideId?: string,
  typeCount = 1,
): { value: string; label: string }[] {
  const reg =
    from === "topologyRegistry"
      ? topologyRegistry
      : from === "initRegistry"
        ? initRegistry
        : modelRegistry;
  const isLab = guideId === "lab";
  return reg
    .list()
    .filter((p) => {
      if (from !== "topologyRegistry") return true;
      const tier = p.tier ?? "basic";
      if (!isLab && tier !== "basic") return false;
      if (REQUIRES_TYPES.has(p.id) && typeCount <= 1) return false;
      return true;
    })
    .map((p) => ({ value: p.id, label: p.label }));
}

function fovPresetValue(fov: number): string {
  if (fov === 0) return "ortho";
  if (fov === 35) return "mild";
  if (fov === 55) return "cinematic";
  return fov < 20 ? "ortho" : fov < 45 ? "mild" : "cinematic";
}

export type AppViewState = {
  sim: SimParams;
  render: RenderParams;
  audio?: AudioParams;
  presetId: string;
  dirty: boolean;
  guideModeId?: string;
};

export class ControlPanel {
  /** Last app snapshot used for display (read-only cache). */
  private sim: SimParams;
  private render: RenderParams;
  private audio: AudioParams;
  private presetId: string;
  private handlers: ControlHandlers;
  private dirty = false;
  private guide: GuideMode;
  private readonly equation: EquationPanel;
  private readonly sectionEls = new Map<string, HTMLElement>();
  private readonly controlHosts = new Map<string, HTMLElement>();
  /**
   * While > 0, a range/select/check is mid-gesture. External syncFromApp
   * is deferred so app→panel round-trips cannot fight the pointer.
   */
  private interactionDepth = 0;
  private pendingSync: AppViewState | null = null;
  /** Tracks last typeCount used to rebuild registry selects. */
  private lastTypeCount = 1;

  constructor(
    sim: SimParams,
    render: RenderParams,
    presetId: string,
    handlers: ControlHandlers,
    guideModeId?: string,
  ) {
    this.sim = cloneSimParams(sim);
    this.render = cloneRenderParams(render);
    this.audio = defaultAudioParams();
    this.presetId = presetId;
    this.handlers = handlers;
    this.guide = getGuideMode(guideModeId);
    this.lastTypeCount = this.sim.typeCount;

    this.buildGuideSwitcher();
    this.populatePresets();
    this.buildSchemaControls();
    this.equation = new EquationPanel($("equation-root"));
    this.bindTransport();
    this.applyGuideMode(this.guide, false);
    this.paintControls();
  }

  getPresetId(): string {
    return this.presetId;
  }

  getGuideModeId(): string {
    return this.guide.id;
  }

  /** True while the user is dragging/typing a control. */
  get isInteracting(): boolean {
    return this.interactionDepth > 0;
  }

  setPlaying(playing: boolean): void {
    $("btn-play").textContent = playing ? "Pause" : "Play";
  }

  setStepsPerFrame(n: number): void {
    const spf = document.getElementById(
      "steps-per-frame",
    ) as HTMLSelectElement | null;
    if (!spf) return;
    const v = String(n);
    if ([...spf.options].some((o) => o.value === v)) spf.value = v;
  }

  setEquation(sim: SimParams): void {
    this.equation.update(sim);
  }

  setGuideMode(id: string, notify = true): void {
    this.guide = getGuideMode(id);
    this.applyGuideMode(this.guide, notify);
    // Registry option lists depend on guide tier
    this.rebuildRegistrySelects();
    this.paintControls();
  }

  /**
   * One-way pull from ExhibitApp. Safe to call every emit.
   * Defers DOM writes while a control is mid-gesture.
   */
  syncFromApp(state: AppViewState): void {
    if (this.interactionDepth > 0) {
      this.pendingSync = {
        sim: cloneSimParams(state.sim),
        render: cloneRenderParams(state.render),
        audio: state.audio
          ? cloneAudioParams(state.audio)
          : cloneAudioParams(this.audio),
        presetId: state.presetId,
        dirty: state.dirty,
        guideModeId: state.guideModeId,
      };
      return;
    }
    this.applyViewState(state);
  }

  /** @deprecated use syncFromApp — kept as alias for call sites in transition */
  loadState(
    sim: SimParams,
    render: RenderParams,
    presetId: string,
    dirty: boolean,
    guideModeId?: string,
  ): void {
    this.syncFromApp({ sim, render, presetId, dirty, guideModeId });
  }

  /**
   * Reflect a preset selection in the panel after the app has applied it.
   * Does not call onPreset (app already owns the mutation).
   */
  reflectPreset(preset: Preset, markClean = true): void {
    this.syncFromApp({
      sim: preset.params,
      render: preset.render,
      presetId: preset.id,
      dirty: !markClean,
    });
    const story = document.getElementById("preset-story");
    const cite = document.getElementById("preset-citation");
    if (story) story.textContent = preset.story;
    if (cite) cite.textContent = preset.citation;
  }

  /** @deprecated use reflectPreset */
  applyPreset(preset: Preset, markClean = true): void {
    this.reflectPreset(preset, markClean);
  }

  private applyViewState(state: AppViewState): void {
    this.sim = cloneSimParams(state.sim);
    this.render = cloneRenderParams(state.render);
    if (state.audio) this.audio = cloneAudioParams(state.audio);
    this.presetId = state.presetId;
    this.dirty = state.dirty;
    if (state.guideModeId && state.guideModeId !== this.guide.id) {
      this.guide = getGuideMode(state.guideModeId);
      this.applyGuideMode(this.guide, false);
    }
    if (PRESETS.some((p) => p.id === state.presetId)) {
      const sel = document.getElementById("preset") as HTMLSelectElement | null;
      if (sel && sel.value !== state.presetId) sel.value = state.presetId;
    }
    if (this.sim.typeCount !== this.lastTypeCount) {
      this.lastTypeCount = this.sim.typeCount;
      this.rebuildRegistrySelects();
    }
    this.paintControls();
    this.updateDirty();
  }

  private bag(): { sim: SimParams; render: RenderParams; audio: AudioParams } {
    return { sim: this.sim, render: this.render, audio: this.audio };
  }

  private beginInteraction(): void {
    this.interactionDepth++;
  }

  private endInteraction(): void {
    this.interactionDepth = Math.max(0, this.interactionDepth - 1);
    if (this.interactionDepth === 0 && this.pendingSync) {
      const pending = this.pendingSync;
      this.pendingSync = null;
      this.applyViewState(pending);
    }
  }

  private emitPatch(def: ControlDef, value: unknown, reset?: boolean): void {
    this.dirty = true;
    this.updateDirty();
    this.handlers.onControlPatch(def.path, value, {
      reset: reset ?? !!def.reset,
    });
  }

  private buildGuideSwitcher(): void {
    const host = document.getElementById("guide-mode-root");
    if (!host) return;
    host.innerHTML = "";
    const label = document.createElement("span");
    label.className = "guide-label";
    label.textContent = "Guide";
    const sel = document.createElement("select");
    sel.id = "guide-mode";
    sel.setAttribute("aria-label", "Interface guide mode");
    for (const m of listGuideModes()) {
      const opt = document.createElement("option");
      opt.value = m.id;
      opt.textContent = m.label;
      opt.title = m.short;
      sel.appendChild(opt);
    }
    sel.value = this.guide.id;

    sel.addEventListener("change", () => {
      this.setGuideMode(sel.value, true);
    });

    host.append(label, sel);
  }

  private populatePresets(): void {
    const sel = document.getElementById("preset") as HTMLSelectElement | null;
    if (!sel) return;
    sel.innerHTML = "";

    // Group by geometry family so the 29-entry list stays scannable.
    // Catalog order is plane → orbit → voxel; groups preserve that order.
    type Group = { label: string; match: (p: Preset) => boolean };
    const groups: Group[] = [
      {
        label: "Plane (2D)",
        match: (p) => p.params.dim === 2,
      },
      {
        label: "Orbit (3D)",
        match: (p) => p.params.dim === 3 && !p.tags.includes("voxel"),
      },
      {
        label: "Voxel lattice",
        match: (p) => p.tags.includes("voxel"),
      },
    ];
    const placed = new Set<string>();
    for (const g of groups) {
      const items = PRESETS.filter((p) => g.match(p) && !placed.has(p.id));
      if (!items.length) continue;
      const og = document.createElement("optgroup");
      og.label = g.label;
      for (const p of items) {
        placed.add(p.id);
        const opt = document.createElement("option");
        opt.value = p.id;
        const hot = p.hotkey ? ` (${p.hotkey})` : "";
        // Titles already carry "3D ·" / "Voxel ·" prefixes — keep them for
        // searchability when the menu is filtered; optgroup supplies the family.
        opt.textContent = `${p.title}${hot}`;
        opt.title = p.subtitle || p.story;
        og.appendChild(opt);
      }
      sel.appendChild(og);
    }
    // Safety: any preset that matched no group
    for (const p of PRESETS) {
      if (placed.has(p.id)) continue;
      const opt = document.createElement("option");
      opt.value = p.id;
      opt.textContent = p.title;
      sel.appendChild(opt);
    }

    sel.addEventListener("change", () => {
      try {
        this.handlers.onPreset(getPreset(sel.value));
      } catch {
        /* ignore */
      }
    });
  }

  private buildSchemaControls(): void {
    const host = $("controls-root");
    host.innerHTML = "";
    this.sectionEls.clear();
    this.controlHosts.clear();

    for (const section of CONTROL_SECTIONS) {
      const block = document.createElement(
        section.collapsible ? "details" : "section",
      );
      block.className = "panel-block";
      block.dataset.section = section.id;
      if (section.collapsible) {
        const sum = document.createElement("summary");
        sum.textContent = section.title;
        block.appendChild(sum);
      } else {
        const h = document.createElement("h2");
        h.textContent = section.title;
        block.appendChild(h);
      }

      for (const def of section.controls) {
        const field = this.buildControl(def);
        block.appendChild(field);
        this.controlHosts.set(def.id, field);
      }

      host.appendChild(block);
      this.sectionEls.set(section.id, block);
    }
  }

  private buildControl(def: ControlDef): HTMLElement {
    const help = getParamHelp(def.glossary);
    const field = document.createElement("div");
    field.className = def.kind === "check" ? "field check" : "field";
    field.dataset.control = def.id;
    if (help) field.title = help.summary;

    const head = document.createElement("div");
    head.className = "field-head";

    let checkInput: HTMLInputElement | null = null;
    if (def.kind === "check") {
      checkInput = document.createElement("input");
      checkInput.type = "checkbox";
      checkInput.id = def.id;
      checkInput.setAttribute("aria-label", def.label);
      checkInput.addEventListener("change", () =>
        this.onCheckInput(def, checkInput!),
      );
      head.appendChild(checkInput);
    }

    const titleRow = document.createElement("div");
    titleRow.className = "field-title";

    const name = document.createElement("label");
    name.className = "field-name";
    name.htmlFor = def.id;
    name.textContent = def.label;
    if (help?.symbol) {
      const sym = document.createElement("span");
      sym.className = "field-symbol";
      sym.textContent = help.symbol;
      name.appendChild(document.createTextNode(" "));
      name.appendChild(sym);
    }
    titleRow.appendChild(name);

    if (def.kind === "range") {
      const val = document.createElement("em");
      val.id = `val-${def.id}`;
      val.className = "field-val";
      titleRow.appendChild(val);
    }
    head.appendChild(titleRow);
    field.appendChild(head);

    if (def.kind === "range") {
      const input = document.createElement("input");
      input.type = "range";
      input.id = def.id;
      input.min = String(def.min ?? 0);
      input.max = String(def.max ?? 1);
      input.step = String(def.step ?? 0.01);
      input.setAttribute("aria-label", def.label);
      // Gesture lock: pointer / keyboard so app sync cannot reset mid-drag
      input.addEventListener("pointerdown", () => this.beginInteraction());
      input.addEventListener("pointerup", () => this.endInteraction());
      input.addEventListener("pointercancel", () => this.endInteraction());
      input.addEventListener("blur", () => {
        // Safety: if pointerup was lost, release on blur
        while (this.interactionDepth > 0) this.endInteraction();
      });
      input.addEventListener("keydown", (ev) => {
        if (
          ev.key === "ArrowLeft" ||
          ev.key === "ArrowRight" ||
          ev.key === "ArrowUp" ||
          ev.key === "ArrowDown" ||
          ev.key === "Home" ||
          ev.key === "End" ||
          ev.key === "PageUp" ||
          ev.key === "PageDown"
        ) {
          if (this.interactionDepth === 0) this.beginInteraction();
        }
      });
      input.addEventListener("keyup", (ev) => {
        if (
          ev.key === "ArrowLeft" ||
          ev.key === "ArrowRight" ||
          ev.key === "ArrowUp" ||
          ev.key === "ArrowDown" ||
          ev.key === "Home" ||
          ev.key === "End" ||
          ev.key === "PageUp" ||
          ev.key === "PageDown"
        ) {
          // Defer end so the paired `input` event runs under the lock
          queueMicrotask(() => this.endInteraction());
        }
      });
      input.addEventListener("input", () => this.onRangeInput(def, input));
      field.appendChild(input);
    } else if (def.kind === "select") {
      const select = document.createElement("select");
      select.id = def.id;
      select.setAttribute("aria-label", def.label);
      this.fillSelectOptions(select, def);
      // Interaction lock — same contract as ranges. HUD/rAF paint must not
      // rewrite select.value while the native menu is open or the control is
      // focused, or the chosen option snaps back to the previous value.
      select.addEventListener("pointerdown", () => this.beginInteraction());
      select.addEventListener("focus", () => {
        if (this.interactionDepth === 0) this.beginInteraction();
      });
      select.addEventListener("change", () => this.onSelectInput(def, select));
      select.addEventListener("blur", () => {
        // Release after change handlers (and any microtask patch→emit) settle.
        queueMicrotask(() => {
          while (this.interactionDepth > 0) this.endInteraction();
        });
      });
      field.appendChild(select);
    }

    // Unlock / dependency hint — painted by paintAvailability each sync
    const unlock = document.createElement("p");
    unlock.className = "field-unlock";
    unlock.hidden = true;
    field.appendChild(unlock);

    // Soft note while still active (e.g. subtle default gravity)
    const note = document.createElement("p");
    note.className = "field-note";
    note.hidden = true;
    field.appendChild(note);

    if (help) {
      const summary = document.createElement("p");
      summary.className = "field-help-summary";
      summary.textContent = help.summary;
      field.appendChild(summary);

      const more = document.createElement("details");
      more.className = "field-more";
      const sum = document.createElement("summary");
      sum.textContent = "More";
      more.appendChild(sum);

      const detail = document.createElement("p");
      detail.className = "field-help-detail";
      detail.textContent = help.detail;
      more.appendChild(detail);

      if (help.low || help.high) {
        const range = document.createElement("p");
        range.className = "field-help-range";
        const parts: string[] = [];
        if (help.low) parts.push(`↓ ${help.low}`);
        if (help.high) parts.push(`↑ ${help.high}`);
        range.textContent = parts.join(" · ");
        more.appendChild(range);
      }

      if (help.interacts_with?.length) {
        const ix = document.createElement("p");
        ix.className = "field-help-ix";
        const names = help.interacts_with
          .map((id) => getParamHelp(id)?.short ?? id)
          .join(", ");
        ix.textContent = `Works with: ${names}`;
        more.appendChild(ix);
      }

      field.appendChild(more);
    }

    return field;
  }

  private fillSelectOptions(select: HTMLSelectElement, def: ControlDef): void {
    select.innerHTML = "";
    const opts = def.optionsFrom
      ? registryOptions(def.optionsFrom, this.guide.id, this.sim.typeCount)
      : (def.options ?? []);
    for (const o of opts) {
      const opt = document.createElement("option");
      opt.value = o.value;
      opt.textContent = o.label;
      select.appendChild(opt);
    }
  }

  private rebuildRegistrySelects(): void {
    for (const def of allControlDefs()) {
      if (def.kind !== "select" || !def.optionsFrom) continue;
      const el = document.getElementById(def.id);
      if (el instanceof HTMLSelectElement) this.fillSelectOptions(el, def);
    }
  }

  private onRangeInput(def: ControlDef, input: HTMLInputElement): void {
    const raw = Number(input.value);
    // Optimistic label update (DOM only) — app owns the real value
    const valEl = document.getElementById(`val-${def.id}`);
    if (valEl) valEl.textContent = fmt(raw, def.digits ?? 3);
    this.emitPatch(def, raw, !!def.reset);
  }

  private onCheckInput(def: ControlDef, input: HTMLInputElement): void {
    this.emitPatch(def, input.checked, !!def.reset);
  }

  private onSelectInput(def: ControlDef, select: HTMLSelectElement): void {
    const raw = select.value;
    if (def.id === "camera_fov_preset") {
      const deg = FOV_PRESET[raw];
      if (deg === undefined) return;
      // Patch the real path (cameraFov), not the sugar control path
      this.dirty = true;
      this.updateDirty();
      this.handlers.onControlPatch("render.cameraFov", deg, { reset: false });
      // Optimistic FOV slider paint
      const fovEl = document.getElementById(
        "camera_fov",
      ) as HTMLInputElement | null;
      if (fovEl) {
        fovEl.value = String(deg);
        const val = document.getElementById("val-camera_fov");
        if (val) val.textContent = String(deg);
      }
      return;
    }
    const asNum = Number(raw);
    const v =
      raw !== "" && Number.isFinite(asNum) && String(asNum) === raw
        ? asNum
        : raw;
    // Selects that rebuild topology/init default to reset unless schema says otherwise
    const reset = def.reset !== false && def.path.startsWith("sim.");
    this.emitPatch(def, v, def.reset !== undefined ? !!def.reset : reset);
  }

  private bindTransport(): void {
    $("btn-play").addEventListener("click", () => this.handlers.onPlayToggle());
    $("btn-step").addEventListener("click", () => this.handlers.onStep());
    $("btn-reset").addEventListener("click", () => this.handlers.onReset());
    $("btn-reseed").addEventListener("click", () => this.handlers.onReseed());
    const shareBtn = document.getElementById("btn-share");
    if (shareBtn)
      shareBtn.addEventListener("click", () => this.handlers.onShare());
    const spf = document.getElementById(
      "steps-per-frame",
    ) as HTMLSelectElement | null;
    if (spf) {
      spf.addEventListener("change", () => {
        this.handlers.onStepsPerFrame(Number(spf.value));
      });
    }
  }

  private applyGuideMode(mode: GuideMode, notify: boolean): void {
    const sel = document.getElementById(
      "guide-mode",
    ) as HTMLSelectElement | null;
    if (sel) sel.value = mode.id;

    document.body.dataset.guide = mode.id;
    document.body.dataset.help = mode.helpDensity;
    applyGuideChrome(mode);

    for (const [id, el] of this.sectionEls) {
      if (el.tagName === "DETAILS") {
        (el as HTMLDetailsElement).open = mode.defaultOpenSections.includes(id);
      }
      // Kiosk: Basic only — camera/lenses/advanced/topology/families/model stay out of the way
      const kioskHide = !!mode.chrome?.hideLabOnly && id !== "basic";
      el.hidden = kioskHide;
    }

    for (const host of this.controlHosts.values()) {
      host.classList.toggle("help-summary", mode.helpDensity === "full");
      host.classList.toggle("help-tooltip", mode.helpDensity === "tooltip");
      host.classList.toggle("help-hidden", mode.helpDensity === "hidden");
      if (mode.helpDensity !== "full") {
        const more = host.querySelector("details.field-more");
        if (more instanceof HTMLDetailsElement) more.open = false;
      }
    }

    this.equation.applyGuide(mode);
    this.equation.update(this.sim);

    if (notify) this.handlers.onGuideMode?.(mode.id);
  }

  private updateDirty(): void {
    const el = document.getElementById("preset-dirty");
    if (el) el.classList.toggle("hidden", !this.dirty);
  }

  /** Paint every schema control from the cached app snapshot. */
  private paintControls(): void {
    const root = this.bag() as never;
    const active = document.activeElement;
    for (const def of allControlDefs()) {
      const raw = getPath(root, def.path);
      const el = document.getElementById(def.id);
      if (!el) continue;
      // Never stomp the widget the user is actively editing (select menus
      // especially: rewriting .value while open cancels the pending choice).
      if (el === active) continue;
      if (def.kind === "range" && el instanceof HTMLInputElement) {
        const n = Number(raw);
        if (el.value !== String(n)) el.value = String(n);
        const val = document.getElementById(`val-${def.id}`);
        if (val) val.textContent = fmt(n, def.digits ?? 3);
      } else if (def.kind === "check" && el instanceof HTMLInputElement) {
        el.checked = Boolean(raw);
      } else if (def.kind === "select" && el instanceof HTMLSelectElement) {
        if (def.id === "camera_fov_preset") {
          const next = fovPresetValue(Number(raw));
          if (el.value !== next) el.value = next;
        } else {
          const s = String(raw ?? "");
          if (el.value !== s) el.value = s;
        }
      }
    }
    this.paintAvailability();
    this.setEquation(this.sim);
  }

  /**
   * Apply self-describing availability: disable inert controls and show
   * "To use this: …" unlock hints from controlAvailability rules.
   */
  private paintAvailability(): void {
    const ctx = {
      sim: this.sim,
      render: this.render,
      audio: this.audio,
    };
    const gated = allControlAvailability(ctx);
    const active = document.activeElement;

    for (const def of allControlDefs()) {
      const host = this.controlHosts.get(def.id);
      if (!host) continue;
      const avail: ControlAvailability =
        gated.get(def.id) ?? controlAvailability(def.id, ctx);

      const input = host.querySelector(`#${CSS.escape(def.id)}`) as
        | HTMLInputElement
        | HTMLSelectElement
        | null;
      const inactive = !avail.active;
      host.classList.toggle("field-inactive", inactive);
      host.classList.toggle("field-has-note", !!avail.note && !inactive);
      if (input) {
        // Don't disable the focused control mid-gesture (drops native menus).
        if (input !== active) {
          input.disabled = inactive;
        }
        if (inactive && avail.reason) {
          input.title = avail.reason;
        } else if (avail.note) {
          input.title = avail.note;
        } else {
          const help = getParamHelp(def.glossary);
          input.title = help?.summary ?? def.label;
        }
        input.setAttribute("aria-disabled", inactive ? "true" : "false");
      }

      const unlockEl = host.querySelector(".field-unlock");
      if (unlockEl instanceof HTMLElement) {
        if (inactive && avail.reason) {
          unlockEl.hidden = false;
          unlockEl.textContent = avail.reason;
        } else {
          unlockEl.hidden = true;
          unlockEl.textContent = "";
        }
      }

      const noteEl = host.querySelector(".field-note");
      if (noteEl instanceof HTMLElement) {
        if (!inactive && avail.note) {
          noteEl.hidden = false;
          noteEl.textContent = avail.note;
        } else {
          noteEl.hidden = true;
          noteEl.textContent = "";
        }
      }
    }
  }
}
